 import { Component, ElementRef, computed, effect, input, model, viewChild } from "@angular/core"
import { NgClass, NgSwitch, NgSwitchCase, NgSwitchDefault } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { FormValueControl } from "@angular/forms/signals"
import { HttpClient } from "@angular/common/http"
import { Observable } from "rxjs"
import { ToastService } from "src/app/services"
import { MatDialog } from "@angular/material/dialog"
import { ConfirmDialogComponent } from "../confirm-dialog/confirm-dialog.component"
import { FileContent, FileContentFactory, PdfFileResult } from "src/app/models"
import { ContextService } from "src/app/services/context.service"
import { FileNamePipe } from "src/app/pipes"
import { MatIcon } from "@angular/material/icon"

@Component({
    selector: "valben-file-input",
    templateUrl: "./file-input.component.html",
    standalone: true,
    imports: [NgClass, NgSwitch, NgSwitchCase, NgSwitchDefault, FormsModule, FileNamePipe, MatIcon],
})
export class FileInputComponent implements FormValueControl<FileContent> {
    value = model(FileContentFactory.create())
    title = input("")
    fileType = input("")
    error = input(false)
    invalid = input(false)
    optional = input(true)
    isPerksPreview = input(false)
    isKng = input(false)
    previewFile = input(true)
    preview = input(false)

    hasError = computed(() => this.error() || this.invalid())

    hovering = false
    urlDraft = ""

    private _browseFile = viewChild<ElementRef<HTMLInputElement>>("browseFile")

    fileTypeCode = computed(() => {
        const contentType = this.value().contentType
        if (contentType.includes("image")) return 1
        if (contentType.includes("video")) return 2
        return 0
    })

    key = input("")
    index = input<number | undefined>(undefined)
    itemObservable = input<(() => Observable<any> | undefined) | undefined>(undefined)
    update = input<((value: any) => void) | undefined>(undefined)

    constructor(
        private _messageService: ToastService,
        private _dialog: MatDialog,
        private _http: HttpClient,
        private _context: ContextService,
    ) {
        effect(() => (this.urlDraft = this.value().url))

        effect(onCleanup => {
            const sub = this.itemObservable()?.()?.subscribe(item => {
                if (!item) return
                const field = item[this.key()]
                const idx = this.index()
                if (idx !== undefined) {
                    if (field === undefined || field.length <= idx) return
                    this.value.set(field[idx])
                } else if (field) {
                    this.value.set(field)
                }
            })
            onCleanup(() => sub?.unsubscribe())
        })
    }

    private _setValue(file: FileContent): void {
        this.value.set(file)
        this.update()?.(file)
    }

    uploadDrop(e: DragEvent): void {
        e.preventDefault()
        this.hovering = false
        if (this.value().url) return
        const file = e.dataTransfer?.files[0]
        if (file) this._readFile(file)
    }

    uploadBrowse(): void {
        if (this.value().url || this.value().fileName) return
        this._browseFile()?.nativeElement.click()
    }

    uploadBrowseChange(e: Event): void {
        const file = (e.target as HTMLInputElement).files?.[0]
        if (file) this._readFile(file)
    }

    commitUrl(): void {
        const url = this.urlDraft.trim()
        if (!url || url === this.value().url) return

        this._http.head(url, { observe: "response" }).subscribe({
            next: res => {
                const contentType = res.headers.get("Content-Type") || ""
                const isImage = contentType.startsWith("image") && this.fileType().includes("image")
                const isVideo = contentType.startsWith("video") && this.fileType().includes("video")
                isImage || isVideo ? this._acceptUrl(url) : this._rejectUrl()
            },
            error: () => {
                const ext = url.split(".").pop()?.toLowerCase()
                const allowed: string[] = []
                if (this.fileType().includes("image")) allowed.push("jpg", "jpeg", "png", "gif", "bmp", "svg", "webp")
                if (this.fileType().includes("video")) allowed.push("mp4", "webm", "ogg", "avi", "mov")
                ext && allowed.includes(ext) ? this._acceptUrl(url) : this._rejectUrl()
            },
        })
    }

    clearUrl(): void {
        this.urlDraft = ""
        this._setValue(FileContentFactory.create())
    }

    removeSelectedFile(e: Event): void {
        e.stopPropagation()
        this._dialog
            .open(ConfirmDialogComponent, {
                width: "35rem",
                data: {
                    header: "Potvrdi brisanje",
                    message: `Želite li stvarno obrisati "${this._displayName()}"?`,
                    confirmText: "Da",
                    cancelText: "Odustani",
                },
            })
            .afterClosed()
            .subscribe(confirmed => {
                if (!confirmed) return
                const input = this._browseFile()?.nativeElement
                if (input) input.value = ""
                this.urlDraft = ""
                this._setValue(FileContentFactory.create())
            })
    }

    openPreview(): void {
        const file = this.value()
        if (file.url) {
            this._context.getKngFile(this._remoteName(file.url)).subscribe({
                next: (result: PdfFileResult) => this._openBlob(this._base64ToBlob(result.fileBytes, result.contentType)),
                error: err => this._fileError("Pregled datoteke nije uspio.", err),
            })
        } else if (file.base64) {
            this._openBlob(this._base64ToBlob(file.base64, file.contentType))
        }
    }

    downloadFile(): void {
        const file = this.value()
        if (file.url) {
            this._context.getKngFile(this._remoteName(file.url)).subscribe({
                next: (result: PdfFileResult) => this._downloadBlob(
                    this._base64ToBlob(result.fileBytes, result.contentType),
                    result.fileName || this._downloadName(file.url),
                ),
                error: err => this._fileError("Preuzimanje datoteke nije uspjelo.", err),
            })
        } else if (file.base64) {
            this._downloadBlob(
                this._base64ToBlob(file.base64, file.contentType),
                file.fileName || `${this.title() || "datoteka"}.pdf`,
            )
        }
    }

    private _fileError(detail: string, err?: unknown): void {
        if (err) console.error(detail, err)
        this._messageService.add({ severity: "error", summary: "Error", detail, life: 10000 })
    }

    private _displayName(): string {
        const file = this.value()
        if (file.fileName) return file.fileName
        if (file.url) return this._downloadName(file.url)
        return this.title()
    }

    /** last two path segments — the API addresses KNG files as "<folder>/<file>" */
    private _remoteName(absoluteUrl: string): string {
        const parts = absoluteUrl.split("/")
        return parts[parts.length - 2] + "/" + parts[parts.length - 1]
    }

    private _downloadName(absoluteUrl: string): string {
        const parts = absoluteUrl.split("/")
        return decodeURIComponent(parts[parts.length - 1] || `${this.title() || "datoteka"}.pdf`)
    }

    private _base64ToBlob(base64: string, contentType: string): Blob {
        const byteCharacters = atob(base64.includes(",") ? base64.split(",").pop() ?? "" : base64)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        return new Blob([new Uint8Array(byteNumbers)], { type: contentType })
    }

    private _openBlob(blob: Blob): void {
        window.open(URL.createObjectURL(blob), "_blank")
    }

    private _downloadBlob(blob: Blob, fileName: string): void {
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = fileName
        a.click()
        URL.revokeObjectURL(url)
    }

    private _acceptUrl(url: string): void {
        this.urlDraft = url
        this._setValue({ url, base64: "", fileName: "", contentType: "" })
    }

    private _rejectUrl(): void {
        this._messageService.add({
            severity: "error",
            summary: "Error",
            detail: "URL datoteka nije podržanog formata.",
            life: 10000,
        })
        this.urlDraft = ""
        this._setValue(FileContentFactory.create())
    }

    private _readFile(file: File): void {
        const type = this.fileType()
        const matchingType = type.includes("/*")
            ? type.replaceAll("/*", "").split(",").some(t => file.type.includes(t))
            : type.includes(file.type)

        if (!matchingType) {
            this._messageService.add({
                severity: "error",
                summary: "Error",
                detail: "Priložena datoteka nije podržanog formata.",
                life: 10000,
            })
            return
        }

        const reader = new FileReader()
        reader.onload = () => {
            this._setValue({
                url: "",
                base64: reader.result as string,
                fileName: file.name,
                contentType: file.type,
            })
        }
        reader.readAsDataURL(file)
    }
}
